import { BaseModel } from '@shared/models/base.model'

import { Team } from './team.model'
import { User } from './user.model'
import { Client } from './client.model'
import { DeviceVendor } from './device-vendor.model'
import { Coordinate } from './coordinate.model'
import { Vehicle } from './vehicle.model'
import { Sensor } from './sensor.model'

export interface DeviceTrackerSensor extends Sensor {
  sensor: Sensor
}

export class Device extends BaseModel {
  id: string
  deviceId: string
  client: Client
  vehicleModel: string
  vehicleNumber: string
  phone: string
  status: DeviceStatus
  statusExt: DeviceStatusExt
  statusList?: DeviceStatus
  addedToTeam: string
  installDate: string
  lastDataReceivedAt: string
  model: DeviceModel
  vendor: DeviceVendor
  sn: string
  hw: string
  sw: string
  port: string
  trackerSensors: DeviceTrackerSensor[]
  trackerData: TrackerData | null
  imei: string
  password: string
  imsi: string
  iccid: string
  blockingMessage: string
  deviceInstallation: DeviceInstallation
  userName: string
  createdAt: string
  createdBy: Pick<User, 'id' | 'fullName'>
  updatedAt: string
  updatedBy: Pick<User, 'id' | 'fullName'>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  notes: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  statuses: any
  team: Team
  isFixWithSpeed?: boolean
  statusUpdatedAt?: string
  usage: string
  isDeactivated: boolean
  isUnavailable: boolean
  contractStartAt: string
  contractFinishAt: string
  contractId: string
  teamId?: number
  professionalInstall: boolean
  ownership: string
  hasCameras: boolean
  statusCameras: StatusCameras
  constructor(src?: Device) {
    super(src)
    const lastCoordinates = this.trackerData && this.trackerData.lastCoordinates
    lastCoordinates && (this.trackerData.lastCoordinates = { lat: +lastCoordinates.lat, lng: +lastCoordinates.lng, ts: lastCoordinates.ts })
  }
}

export type DeviceEdit = Partial<Device> & { teamId?: number }

export interface DeviceModel {
  id: number
  name: string
  alias: string
  usage: string
  protocol: string | null
}

export enum StatusCameras {
  Online = 'ONLINE',
  Offline = 'OFFLINE',
}

export interface TrackerData {
  id?: number
  address: string
  batteryVoltage: number
  batteryVoltagePercentage: number
  solarChargingStatus: boolean
  externalVoltage: number
  engineHours: number
  gpsStatus: boolean
  lastCoordinates: Coordinate
  lastDataReceived: string
  mileage: number
  speed?: number
  angle?: number
  standsIgnition: boolean
  status: DeviceStatus
  territory?: string
  temperatureLevel: number
  obdData: TrackerOBDData
}

export interface TrackerOBDData {
  rpm: number
  accumulatingFuel: number
  airInflowTemperature: number
  airInput: number
  airPressure: number
  coolantTemperature: number
  engineLoad: number
  instantFuel: number
  remainFuelRate: number
  throttlePosition: number
}

export interface RouteArea {
  id: number
  name: string
}

export class DeviceInstallation extends BaseModel {
  id: string
  device: Device
  deviceId: string
  installDate: string
  uninstallDate: string
  vehicle: Vehicle
  vehicleId: string
  odometer: number
}

export enum DeviceStatus {
  InStockAdmin = 'inStockAdmin',
  InStockReseller = 'inStockReseller',
  InStock = 'inStock',
  Driving = 'driving',
  Idle = 'idle',
  Stopped = 'stopped',
  Towing = 'towing',
  Offline = 'offline',
  Unavailable = 'unavailable',
  Deleted = 'deleted',
  Returned = 'returned',
}

export const DeviceStatusLabel = {
  [DeviceStatus.InStock]: 'ADMIN.DEVICES.STATUSES.IN_STOCK',
  [DeviceStatus.Driving]: 'ADMIN.DEVICES.STATUSES.DRIVING',
  [DeviceStatus.Idle]: 'ADMIN.DEVICES.STATUSES.IDLE',
  [DeviceStatus.Stopped]: 'ADMIN.DEVICES.STATUSES.STOPPED',
  [DeviceStatus.Towing]: 'ADMIN.DEVICES.STATUSES.TOWING',
  [DeviceStatus.Offline]: 'ADMIN.DEVICES.STATUSES.OFFLINE',
  [DeviceStatus.Unavailable]: 'ADMIN.DEVICES.STATUSES.UNAVAILABLE',
  [DeviceStatus.Deleted]: 'ADMIN.DEVICES.STATUSES.DELETED',
  [DeviceStatus.Returned]: 'ADMIN.DEVICES.STATUSES.RETURNED',
} as const

export enum DeviceStatusExt {
  Ok = 'ok',
  NoGps = 'noGps',
  NoPower = 'noPower',
  Offline = 'offline',
}

export const DeviceStatusExtLabel = {
  [DeviceStatusExt.Ok]: 'ADMIN.DEVICES.EXT_STATUSES.OK',
  [DeviceStatusExt.NoGps]: 'ADMIN.DEVICES.EXT_STATUSES.NO_GPS',
  [DeviceStatusExt.NoPower]: 'ADMIN.DEVICES.EXT_STATUSES.NO_POWER',
  [DeviceStatusExt.Offline]: 'ADMIN.DEVICES.EXT_STATUSES.OFFLINE',
} as const

export enum DeviceUsage {
  Vehicle = 'vehicle',
  Personal = 'personal',
  Asset = 'asset',
  Satellite = 'satellite',
}

export const DeviceUsageLabel = {
  [DeviceUsage.Vehicle]: 'ADMIN.DEVICES.USAGES.VEHICLE',
  [DeviceUsage.Personal]: 'ADMIN.DEVICES.USAGES.PERSONAL',
  [DeviceUsage.Asset]: 'ADMIN.DEVICES.USAGES.ASSET',
  [DeviceUsage.Satellite]: 'ADMIN.DEVICES.USAGES.SATELLITE',
} as const

export enum DeviceActivity {
  Active,
  Deactivated,
}

export const DeviceActivityLabel = {
  [DeviceActivity.Active]: 'ADMIN.DEVICES.ACTIVITY_STATUSES.ACTIVE',
  [DeviceActivity.Deactivated]: 'ADMIN.DEVICES.ACTIVITY_STATUSES.DEACTIVATED',
}

export enum DeviceOwnership {
  Linxio = 'linxio',
  Client = 'client',
}

export const DeviceOwnershipLabel = {
  [DeviceOwnership.Linxio]: 'ADMIN.DEVICES.OWNERSHIPS.LINXIO',
  [DeviceOwnership.Client]: 'ADMIN.DEVICES.OWNERSHIPS.CLIENT',
} as const

export class DeviceCoordinates extends BaseModel {
  date: string
  coordinates: Coordinate[]

  constructor(src?: DeviceCoordinates) {
    super(src)

    const toNumber = ({ lat, lng, ts }: Coordinate) => ({ lat: +lat, lng: +lng, ts })
    this.coordinates = this.coordinates.map(toNumber)
  }
}

export interface DeviceInstallUninstallInputData {
  vehicleId: number | string
  status?: DeviceStatus
}

export interface LinkDeviceToSensorData {
  sensorId: string
}

export enum DeviceContract {
  Deactivated = 'deactivated',
  Unavailable = 'unavailable',
  Contract = 'contract',
  Phone = 'device.phone.changed',
  InstallationHistory = 'install',
}

export interface DeviceReplacement {
  id: number
  client: Client
  deviceOld: Device
  deviceNew: Device
  imeiNew: string
  imeiOld: string
  createdAt: string
  createdBy: User
  reason: string
  teamId: number
  isReturned: boolean
}

export interface DeviceVehicleHistory extends Pick<DeviceInstallation, 'vehicleId' | 'installDate' | 'uninstallDate'> {
  vehicleRegNo: string
}

export interface DeviceHistory {
  id: string
  createdByName: string
  payload: Device[] | Vehicle | string
  createdAt: string
  type: string
}

export interface CameraDevice {
  type: string
  url: string
  isAvailable: boolean
  expiredAt: string
  isUrlUsed?: boolean
}

export interface CamerasEventsType {
  id: number
  name: string
  label: string
}

export interface CamerasEvent {
  id: number
  deviceId: number
  vehicle: Vehicle
  driver: User
  startedAt: string
  finishedAt: string
  type: CamerasEventsType
  files: CamerasEventFile[]
}

export interface CamerasEventFile {
  id: number
  eventId: number
  url: string
  startedAt: string
  finishedAt: string
  fileType: number
  cameraType: string
}

export interface DeviceContractUpdateData {
  contractId: string
  contractStartAt: string
  contractFinishAt: string
}
